import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { inquiryAPI, publicAPI } from '../services/api';
import PortalFooter from '../components/PortalFooter';
import PublicNavBar from '../components/PublicNavBar';
import { gradientHero } from '../theme';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const DEFAULT_STEPS = ['Art des Projekts', 'Projektdetails', 'Projektinfo', 'Ihre Kontaktdaten'];

const DEFAULT_INBOUND_TYPES = [
  'Bestellannahme',
  'Helpdesk / Technische Hotline',
  'Sekretariatsservice',
  'Reservierung / Ticketing',
  'Notfallservice / 24h-Hotline',
  'Sonstiges',
];

const DEFAULT_OUTBOUND_TYPES = [
  'Telesales (Verkauf am Telefon)',
  'Terminvereinbarung',
  'Leadmanagement / Leadgenerierung',
  'Adressqualifizierung',
  'Kundenzufriedenheitsbefragung',
  'Marktforschung / Mystery Calls',
  'Kundenrückgewinnung',
  'Sonstiges',
];

const DEFAULT_SERVICE_HOUR_OPTIONS = [
  'Mo. – Fr. normale Bürozeiten (9–18 Uhr)',
  'Mo. – Fr. + Samstag',
  'Mo. – Fr. + Wochenende',
  '24 Stunden / 7 Tage pro Woche',
  'Individuell / Projektabhängig',
];

const GESELLSCHAFTSFORMEN = ['GmbH', 'AG', 'UG (haftungsbeschränkt)', 'GmbH & Co. KG', 'KG', 'OHG', 'GbR', 'Einzelunternehmen (e.K.)', 'e.V.', 'Sonstige'];

const SALUTATIONS = ['Herr', 'Frau', 'Herr Dr.', 'Frau Dr.', 'Herr Prof.', 'Frau Prof.'];

const EMPTY_FORM = {
  direction: '',
  customer_segment: '',
  recording_required: false,
  inbound_type: '',
  outbound_type: '',
  outbound_address_source: '',
  call_volume: '',
  service_hours: '',
  languages: '',
  quality_requirements: '',
  budget: '',
  title: '',
  description: '',
  zip_code: '',
  city: '',
  region: '',
  salutation: '',
  contact_name: '',
  company_name: '',
  society_form: '',
  contact_email: '',
  contact_phone: '',
  privacy_accepted: false,
};

const SubmitInquiryPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [steps, setSteps] = useState<string[]>(DEFAULT_STEPS);
  const [inboundTypes, setInboundTypes] = useState<string[]>(DEFAULT_INBOUND_TYPES);
  const [outboundTypes, setOutboundTypes] = useState<string[]>(DEFAULT_OUTBOUND_TYPES);
  const [serviceHourOptions, setServiceHourOptions] = useState<string[]>(DEFAULT_SERVICE_HOUR_OPTIONS);

  useEffect(() => {
    publicAPI.getFormConfig().then((res) => {
      const cfg = res.data?.data;
      if (!cfg) return;
      if (cfg.step_labels?.length === 4) setSteps(cfg.step_labels);
      if (cfg.inbound_types?.length) setInboundTypes(cfg.inbound_types);
      if (cfg.outbound_types?.length) setOutboundTypes(cfg.outbound_types);
      if (cfg.service_hours?.length) setServiceHourOptions(cfg.service_hours);
    }).catch(() => {});
  }, []);

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const validateStep = (): string | null => {
    if (activeStep === 0) {
      if (!formData.direction) return 'Bitte wählen Sie Inbound oder Outbound.';
      if (!formData.customer_segment) return 'Bitte wählen Sie das Kundensegment (B2B/B2C).';
      if ((formData.direction === 'inbound' || formData.direction === 'both') && !formData.inbound_type) return 'Bitte wählen Sie die Art des Inbound.';
      if ((formData.direction === 'outbound' || formData.direction === 'both') && !formData.outbound_type) return 'Bitte wählen Sie die Art des Outbound.';
      if ((formData.direction === 'outbound' || formData.direction === 'both') && !formData.outbound_address_source) return 'Bitte geben Sie an, wer die Adressen liefert.';
    }
    if (activeStep === 1) {
      if (!formData.call_volume) return 'Bitte geben Sie das erwartete Anruf- oder Adressvolumen an.';
      if (!formData.service_hours) return 'Bitte wählen Sie die gewünschten Servicezeiten.';
    }
    if (activeStep === 2) {
      if (!formData.title) return 'Bitte geben Sie einen Projekttitel ein.';
      if (!formData.description) return 'Bitte beschreiben Sie Ihr Projekt.';
      if (!formData.zip_code || !formData.city) return 'Bitte geben Sie PLZ und Ort ein.';
    }
    if (activeStep === 3) {
      if (!formData.salutation) return 'Bitte wählen Sie eine Anrede.';
      if (!formData.contact_name) return 'Bitte geben Sie Ihren Namen ein.';
      if (!formData.contact_email) return 'Bitte geben Sie Ihre E-Mail-Adresse ein.';
      if (!formData.contact_phone) return 'Bitte geben Sie Ihre Telefonnummer ein (für die Rückmeldung).';
      if (!formData.privacy_accepted) return 'Bitte akzeptieren Sie die Datenschutzerklärung.';
    }
    return null;
  };

  const handleNext = () => {
    const error = validateStep();
    if (error) { showError(error); return; }
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validateStep();
    if (error) { showError(error); return; }
    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        region: formData.city ? `${formData.zip_code} ${formData.city}` : formData.region,
        category_id: formData.direction === 'inbound' ? 1 : 2,
      };
      await inquiryAPI.submitInquiry(payload);
      setSubmitted(true);
      showSuccess('Gesuch erfolgreich eingereicht! Unser Team meldet sich in Kürze.');
    } catch (err: any) {
      showError(err.response?.data?.message || 'Fehler beim Einreichen des Gesuchs');
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
              Vielen Dank!
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
              Ihr Gesuch wurde erfolgreich eingereicht.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
              Unser Team meldet sich innerhalb von 24 Stunden per Telefon oder E-Mail bei Ihnen, um Ihr Gesuch zu bestätigen und zu veröffentlichen.
            </Typography>
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <strong>Nächste Schritte:</strong> Nach der Freigabe werden sich bis zu 4 geprüfte Call-Center-Dienstleister direkt bei Ihnen melden.
            </Alert>
            <Button variant="contained" size="large" onClick={() => navigate('/')}>
              Zurück zur Startseite
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <PublicNavBar />

      <Box sx={{ background: gradientHero, color: '#fff', py: { xs: 5, md: 7 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 2 }}>
            Kostenloses Gesuch aufgeben
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
            In 4 Schritten zum passenden Call-Center-Partner – kostenlos und unverbindlich.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md">
        <Box py={6}>
          <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: '1px solid #e0e0e0' }}>
            <form onSubmit={handleSubmit}>

              {activeStep === 0 && (
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                    Art des Projekts
                  </Typography>

                  <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                    <FormLabel sx={{ fontWeight: 600, mb: 1 }}>
                      Sie suchen ein Callcenter, um Anrufe... *
                    </FormLabel>
                    <RadioGroup row name="direction" value={formData.direction} onChange={handleChange}>
                      <FormControlLabel value="inbound" control={<Radio />} label="Entgegenzunehmen (Inbound)" />
                      <FormControlLabel value="outbound" control={<Radio />} label="Aktiv zu tätigen (Outbound / Rebound)" />
                      <FormControlLabel value="both" control={<Radio />} label="Beides (Inbound & Outbound)" />
                    </RadioGroup>
                  </FormControl>

                  {(formData.direction === 'inbound' || formData.direction === 'both') && (
                    <FormControl fullWidth sx={{ mb: 3 }} required>
                      <InputLabel>Art des Inbound *</InputLabel>
                      <Select name="inbound_type" value={formData.inbound_type} onChange={handleChange} label="Art des Inbound *">
                        {inboundTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}

                  {(formData.direction === 'outbound' || formData.direction === 'both') && (
                    <>
                      <FormControl fullWidth sx={{ mb: 3 }} required>
                        <InputLabel>Art des Outbound *</InputLabel>
                        <Select name="outbound_type" value={formData.outbound_type} onChange={handleChange} label="Art des Outbound *">
                          {outboundTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                        <FormLabel sx={{ fontWeight: 600, mb: 1 }}>Wer liefert die Adressen für den Outbound? *</FormLabel>
                        <RadioGroup name="outbound_address_source" value={formData.outbound_address_source} onChange={handleChange}>
                          <FormControlLabel value="customer" control={<Radio />} label="Werden vom Auftraggeber gestellt" />
                          <FormControlLabel value="callcenter" control={<Radio />} label="Sollen vom Callcenter gestellt werden" />
                        </RadioGroup>
                      </FormControl>
                    </>
                  )}

                  <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                    <FormLabel sx={{ fontWeight: 600, mb: 1 }}>Ihr Kundensegment *</FormLabel>
                    <RadioGroup row name="customer_segment" value={formData.customer_segment} onChange={handleChange}>
                      <FormControlLabel value="B2B" control={<Radio />} label="B2B – Geschäftskunden" />
                      <FormControlLabel value="B2C" control={<Radio />} label="B2C – Privatkunden" />
                      <FormControlLabel value="B2B+B2C" control={<Radio />} label="Beides" />
                    </RadioGroup>
                  </FormControl>

                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormLabel sx={{ fontWeight: 600, mb: 1 }}>Qualitätssicherung</FormLabel>
                    <FormControlLabel
                      control={<Checkbox name="recording_required" checked={formData.recording_required} onChange={handleChange} />}
                      label="Inkl. Gesprächsmitschnitt & Analyse"
                    />
                  </FormControl>
                </Box>
              )}

              {activeStep === 1 && (
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                    Projektdetails
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label={formData.direction === 'outbound' ? 'Menge zu bearbeitender Adressen (z.B. 500/Monat) *' : 'Wieviele Anrufe werden erwartet? (z.B. 1.000/Monat) *'}
                        name="call_volume"
                        value={formData.call_volume}
                        onChange={handleChange}
                        required
                        placeholder={formData.direction === 'outbound' ? 'z.B. 500 Adressen/Monat' : 'z.B. 1.000 Anrufe/Monat'}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormControl fullWidth required>
                        <InputLabel>Servicezeiten *</InputLabel>
                        <Select name="service_hours" value={formData.service_hours} onChange={handleChange} label="Servicezeiten *">
                          {serviceHourOptions.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Sprachen"
                        name="languages"
                        value={formData.languages}
                        onChange={handleChange}
                        placeholder="z.B. Deutsch, oder Deutsch + Englisch"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Qualitätsanforderungen & Besonderheiten"
                        name="quality_requirements"
                        value={formData.quality_requirements}
                        onChange={handleChange}
                        multiline
                        rows={3}
                        placeholder="z.B. ISO-Zertifizierung, DSGVO-Compliance, Branchenkenntnisse, Skript vorhanden..."
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Geschätztes Budget (EUR/Monat)"
                        name="budget"
                        type="number"
                        value={formData.budget}
                        onChange={handleChange}
                        inputProps={{ min: 0, step: 100 }}
                        placeholder="z.B. 3000"
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeStep === 2 && (
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                    Ihr Projekt
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Projekttitel *"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        placeholder="z.B. Outbound B2B Leadgenerierung für SaaS-Produkt"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Projektbeschreibung *"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        required
                        multiline
                        rows={5}
                        placeholder="Beschreiben Sie Ihre Anforderungen, Ziele, Zielgruppe und spezielle Wünsche..."
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        label="PLZ *"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleChange}
                        required
                        placeholder="10115"
                        inputProps={{ maxLength: 10 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                      <TextField
                        fullWidth
                        label="Ort *"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        placeholder="Berlin"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormControl fullWidth>
                        <InputLabel>Land</InputLabel>
                        <Select name="region" value={formData.region} onChange={handleChange} label="Land">
                          <MenuItem value="Deutschland">Deutschland</MenuItem>
                          <MenuItem value="Österreich">Österreich</MenuItem>
                          <MenuItem value="Schweiz">Schweiz</MenuItem>
                          <MenuItem value="EU-Ausland">EU-Ausland</MenuItem>
                          <MenuItem value="International">International</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeStep === 3 && (
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                    Ihre Kontaktdaten
                  </Typography>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Wir melden uns innerhalb von 24 Stunden telefonisch oder per E-Mail bei Ihnen zurück, um Ihr Gesuch zu bestätigen.
                  </Alert>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth required>
                        <InputLabel>Anrede *</InputLabel>
                        <Select name="salutation" value={formData.salutation} onChange={handleChange} label="Anrede *">
                          {SALUTATIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                      <TextField
                        fullWidth
                        label="Ihr Name *"
                        name="contact_name"
                        value={formData.contact_name}
                        onChange={handleChange}
                        required
                        placeholder="Max Mustermann"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Firmenname *"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        required
                        placeholder="Mustermann GmbH"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormControl fullWidth>
                        <InputLabel>Gesellschaftsform</InputLabel>
                        <Select name="society_form" value={formData.society_form} onChange={handleChange} label="Gesellschaftsform">
                          <MenuItem value="">Bitte wählen</MenuItem>
                          {GESELLSCHAFTSFORMEN.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="E-Mail-Adresse *"
                        name="contact_email"
                        type="email"
                        value={formData.contact_email}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Telefonnummer *"
                        name="contact_phone"
                        value={formData.contact_phone}
                        onChange={handleChange}
                        required
                        placeholder="+49 30 12345678"
                        helperText="Für die Rückbestätigung durch unser Team"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 1 }} />
                      <FormControlLabel
                        control={
                          <Checkbox name="privacy_accepted" checked={formData.privacy_accepted} onChange={handleChange} />
                        }
                        label={
                          <Typography variant="body2">
                            Ich akzeptiere die{' '}
                            <span
                              style={{ color: '#667eea', cursor: 'pointer', textDecoration: 'underline' }}
                              onClick={() => window.open('/privacy', '_blank')}
                            >
                              Datenschutzerklärung
                            </span>{' '}
                            und bin damit einverstanden, dass meine Daten für die Vermittlung verwendet werden. *
                          </Typography>
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                <Button
                  variant="outlined"
                  onClick={activeStep === 0 ? () => navigate('/') : handleBack}
                  disabled={isLoading}
                >
                  {activeStep === 0 ? 'Abbrechen' : 'Zurück'}
                </Button>

                {activeStep < steps.length - 1 ? (
                  <Button variant="contained" size="large" onClick={handleNext}>
                    Weiter →
                  </Button>
                ) : (
                  <Button variant="contained" size="large" type="submit" disabled={isLoading} sx={{ px: 5 }}>
                    {isLoading ? <CircularProgress size={24} /> : 'Gesuch absenden'}
                  </Button>
                )}
              </Box>
            </form>
          </Paper>
        </Box>
      </Container>

      <PortalFooter />
    </Box>
  );
};

export default SubmitInquiryPage;
